import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getProjectDocuments, uploadProjectDocument, deleteProjectDocument,
  downloadProjectDocument,
} from '../api';
import useConfirm from '../hooks/useConfirm';
import { ROLE_RANK } from '../permissions';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import ConfirmDialog from './ConfirmDialog';

/* ──────────────────────────────────────────────────────────────────
 * Project Documents — a general construction-document store whose
 * first and most important kind is the SIGNED pay application.
 *
 * WHY THE PAY-APP TABLE IS SEPARATE AND FIRST. The executed, notarised
 * AFP — the one Cindy and Pat sign — lived only inside Dropbox Sign.
 * Summit kept a 40-character envelope id pointing at it and nothing
 * else, and the unsigned copy on the VM is deleted on a 30-day purge.
 * So the question this screen has to answer at a glance is not "what
 * files exist" but "is there a signed copy on file for every pay app
 * we billed" — and a flat file list answers that only by ABSENCE,
 * which is exactly the thing people fail to notice. Pairing generated
 * against signed, per period, makes a gap loud.
 *
 * ⚠️ ONLY THE EXECUTED DOCUMENT IS STORED (Duncan, 2026-09-03). The
 * unsigned pdf the CE Agent generates is deliberately NOT kept in Summit:
 * per agent.py it never round-tripped, and now that finalize freezes each
 * line's identity and scheduled value onto billing_period_lines, Summit
 * can already reconstruct exactly what an AFP claimed. The generated pdf
 * would add formatting, not information. The SIGNED copy is different --
 * it carries signatures and notarisation that exist nowhere else.
 *
 * So there is no "Generated" column. billing_periods.afp_pdf_bytes and
 * POST /api/billing/:id/afp-pdf stay permanently dormant, and the list
 * endpoint still reports `generated` for that column's sake if the
 * decision is ever revisited -- it is simply not rendered.
 *
 * Trade-off accepted: a pay app generated and sent but never signed has
 * no surviving rendering after the VM's 30-day purge. Its numbers live on
 * in the frozen lines; only the document as the owner received it is gone.
 * ────────────────────────────────────────────────────────────────── */

const DOC_TYPE_LABELS = {
  pay_app: 'Pay app',
  contract: 'Contract',
  change_order: 'Change order',
  drawing: 'Drawing',
  submittal: 'Submittal',
  permit: 'Permit',
  rfi: 'RFI',
  closeout: 'Closeout',
  photo: 'Photo',
  other: 'Other',
};

// Signature states as the webhook records them, mapped to something a human
// reads. Anything unrecognised falls through to the raw value rather than
// being hidden — an unmapped state is information, not noise.
const SIG_LABELS = {
  sent: { text: 'awaiting signatures', tone: 'muted' },
  partially_signed: { text: 'partly signed', tone: 'warning' },
  all_signed: { text: 'signed', tone: 'success' },
  declined: { text: 'declined', tone: 'danger' },
  error: { text: 'error', tone: 'danger' },
  canceled: { text: 'cancelled', tone: 'muted' },
};

const fmtMoney = (v) => `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (!b) return '–';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d);
    const sameYear = dt.getFullYear() === new Date().getFullYear();
    return dt.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
    });
  } catch { return String(d); }
};

const TONE_COLOR = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  muted: 'var(--text-3)',
};

const Pill = ({ tone = 'muted', children, title }) => (
  <span
    title={title}
    style={{
      padding: '1px 7px', borderRadius: 5, fontSize: 12.5, whiteSpace: 'nowrap',
      background: `color-mix(in oklch, ${TONE_COLOR[tone]} 12%, var(--bg-surface))`,
      color: TONE_COLOR[tone],
    }}
  >{children}</span>
);

const LinkButton = ({ onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
      color: 'var(--accent)', fontSize: 14, fontFamily: 'inherit', textAlign: 'left',
    }}
  >{children}</button>
);

const Th = ({ children, align = 'left' }) => (
  <th style={{
    textAlign: align, padding: '6px 8px', fontSize: 10, fontWeight: 600,
    color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.07em',
    borderBottom: '1px solid var(--border-default)', whiteSpace: 'nowrap',
  }}>{children}</th>
);

const Td = ({ children, align = 'left', mono = false }) => (
  <td
    className={mono ? 'mono' : undefined}
    style={{
      textAlign: align, padding: '9px 8px', fontSize: 14, color: 'var(--text-2)',
      borderTop: '1px solid var(--border-subtle)', verticalAlign: 'top',
    }}
  >{children}</td>
);

const Card = ({ title, subtitle, side, children }) => (
  <div style={{
    background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
    borderRadius: 10, boxShadow: 'var(--shadow-xs)', overflow: 'hidden',
  }}>
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '13px 16px', borderBottom: '1px solid var(--border-subtle)',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)' }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 13.5, color: 'var(--text-3)', marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      <div style={{ flex: 1 }} />
      {side}
    </div>
    {children}
  </div>
);

/**
 * Pure presentation. Exported so every state can be rendered offline against
 * the SHIPPED component — Summit cannot be run locally, and the two things most
 * worth getting right here (a finalized pay app with no signed copy is
 * highlighted, and Remove is hidden below CM) are invisible to a build.
 */
export const DocumentsView = ({
  payApps = [], documents = [], storage = {},
  error = null, notice = null, uploading = false, docType = 'other',
  canDelete = false, busyId = null,
  onDocTypeChange = () => {}, onUploadClick = () => {},
  onDownloadDocument = () => {}, onDelete = () => {},
  fileInput = null, confirmDialog = null, search = '',
}) => {
  const missingSigned = payApps.filter((p) => p.status === 'Finalized' && !p.signed).length;
  // The top bar's search (lib/pageSearch) narrows the rows shown. The missing-copy count and the
  // empty states stay on everything filed, so a search can never make a gap look closed.
  const shownPayApps = payApps.filter((p) => matchesSearch(search, p.period_name, p.status, p.signed?.filename,
    p.status === 'Finalized' && !p.signed ? 'missing' : null,
    SIG_LABELS[p.signature_status]?.text || p.signature_status || 'not sent'));
  const shownDocuments = documents.filter((d) => matchesSearch(search, d.title, d.filename,
    DOC_TYPE_LABELS[d.doc_type] || d.doc_type, d.notes, d.uploaded_by, d.immutable ? 'record' : null));

  return (
    <div style={{ padding: '18px 4px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {error && (
        <div style={{
          padding: '10px 12px', borderRadius: 8, fontSize: 14,
          background: 'color-mix(in oklch, var(--danger) 6%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--danger) 28%, var(--border-default))',
          color: 'var(--danger)',
        }}>{error}</div>
      )}

      {storage.configured === false && (
        <div style={{
          padding: '10px 12px', borderRadius: 8, fontSize: 14,
          background: 'color-mix(in oklch, var(--warning) 6%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--warning) 28%, var(--border-default))',
          color: 'var(--text-2)',
        }}>
          <span style={{ color: 'var(--warning)', fontWeight: 500 }}>Document storage is not configured.</span>{' '}
          Existing pay-app PDFs still download, but uploads and signed-copy archiving are off
          until <span className="mono">DOCS_STORAGE_ACCOUNT</span> is set on the App Service.
        </div>
      )}

      {notice && (
        <div style={{
          padding: '9px 12px', borderRadius: 8, fontSize: 14,
          background: `color-mix(in oklch, ${TONE_COLOR[notice.tone]} 7%, var(--bg-surface))`,
          border: `1px solid color-mix(in oklch, ${TONE_COLOR[notice.tone]} 26%, var(--border-default))`,
          color: 'var(--text-2)',
        }}>{notice.text}</div>
      )}

      <Card
        title="Pay applications"
        subtitle={payApps.length === 0
          ? 'No billing periods on this project yet.'
          : missingSigned > 0
            ? `${missingSigned} finalized pay app${missingSigned === 1 ? '' : 's'} with no executed copy on file`
            : 'Every finalized pay app has its executed copy on file'}
        side={missingSigned > 0 ? <Pill tone="danger">{missingSigned} missing</Pill> : null}
      >
        {payApps.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <Th>Pay app</Th>
                  <Th align="right">Amount</Th>
                  <Th>Executed copy</Th>
                  <Th>Signatures</Th>
                </tr>
              </thead>
              <tbody>
                {shownPayApps.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{noMatchText(search).body}</td></tr>
                )}
                {shownPayApps.map((p) => {
                  const sig = SIG_LABELS[p.signature_status];
                  const gap = p.status === 'Finalized' && !p.signed;
                  return (
                    <tr key={p.billing_period_id} style={gap ? {
                      background: 'color-mix(in oklch, var(--danger) 5%, transparent)',
                    } : undefined}>
                      <Td>
                        <span style={{ color: 'var(--text-1)' }}>{p.period_name}</span>
                        {p.period_end_date && (
                          <span style={{ color: 'var(--text-3)' }}> · {fmtDate(p.period_end_date)}</span>
                        )}
                        {p.status !== 'Finalized' && (
                          <span style={{ color: 'var(--text-4)', fontSize: 13 }}> · {p.status}</span>
                        )}
                      </Td>
                      <Td align="right" mono>{fmtMoney(p.amount)}</Td>
                      <Td>
                        {p.signed ? (
                          <LinkButton
                            title={`${fmtBytes(p.signed.size_bytes)} · filed ${fmtDate(p.signed.received_at)}`}
                            onClick={() => onDownloadDocument({
                              id: p.signed.document_id, filename: p.signed.filename,
                            })}
                          >PDF</LinkButton>
                        ) : (
                          <span style={{ color: gap ? 'var(--danger)' : 'var(--text-4)' }}>
                            {gap ? 'missing' : '–'}
                          </span>
                        )}
                      </Td>
                      <Td>
                        {sig ? (
                          <Pill tone={sig.tone} title={p.signature_completed_at
                            ? `completed ${fmtDate(p.signature_completed_at)}` : undefined}>
                            {sig.text}{p.signature_test_mode ? ' (test)' : ''}
                          </Pill>
                        ) : p.signature_status ? (
                          <Pill tone="muted">{p.signature_status}</Pill>
                        ) : <span style={{ color: 'var(--text-4)' }}>not sent</span>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Documents"
        subtitle="Contracts, drawings, submittals, permits and anything else filed against this project."
        side={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <select
              value={docType}
              onChange={(e) => onDocTypeChange(e.target.value)}
              disabled={uploading}
              style={{
                height: 32, padding: '0 8px', borderRadius: 8, fontSize: 14,
                fontFamily: 'inherit', color: 'var(--text-2)',
                background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
              }}
            >
              {Object.entries(DOC_TYPE_LABELS).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
            {fileInput}
            <button
              type="button"
              onClick={onUploadClick}
              disabled={uploading || storage.configured === false}
              style={{
                height: 32, padding: '0 12px', borderRadius: 8, fontSize: 14, fontWeight: 500,
                fontFamily: 'inherit', color: 'var(--text-2)',
                background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
                boxShadow: 'var(--shadow-xs)',
                cursor: uploading ? 'wait' : 'pointer',
                opacity: (uploading || storage.configured === false) ? 0.6 : 1,
              }}
            >{uploading ? 'Uploading…' : 'Upload'}</button>
          </div>
        )}
      >
        {documents.length === 0 ? (
          <div style={{ padding: '18px 16px', fontSize: 14, color: 'var(--text-3)' }}>
            Nothing filed yet. Pick a type and upload a file to start.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <Th>Document</Th>
                  <Th>Type</Th>
                  <Th>Added</Th>
                  <Th align="right">Size</Th>
                  <Th align="right"> </Th>
                </tr>
              </thead>
              <tbody>
                {shownDocuments.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '28px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-3)' }}>{noMatchText(search).body}</td></tr>
                )}
                {shownDocuments.map((d) => (
                  <tr key={d.id}>
                    <Td>
                      <LinkButton title={d.filename} onClick={() => onDownloadDocument(d)}>
                        {d.title || d.filename}
                      </LinkButton>
                      {d.immutable ? (
                        <span style={{ marginLeft: 8 }}>
                          <Pill tone="muted" title="A signed record — cannot be removed">record</Pill>
                        </span>
                      ) : null}
                      {d.notes && (
                        <div style={{ fontSize: 13, color: 'var(--text-4)', marginTop: 3 }}>{d.notes}</div>
                      )}
                    </Td>
                    <Td>{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</Td>
                    <Td>
                      {fmtDate(d.created_at)}
                      {d.uploaded_by && (
                        <div style={{ fontSize: 13, color: 'var(--text-4)' }}>{d.uploaded_by}</div>
                      )}
                    </Td>
                    <Td align="right" mono>{fmtBytes(d.size_bytes)}</Td>
                    <Td align="right">
                      {canDelete && !d.immutable && (
                        <button
                          type="button"
                          onClick={() => onDelete(d)}
                          disabled={busyId === d.id}
                          style={{
                            background: 'none', border: 'none', padding: 0,
                            cursor: busyId === d.id ? 'wait' : 'pointer',
                            color: 'var(--text-4)', fontSize: 14, fontFamily: 'inherit',
                          }}
                        >{busyId === d.id ? '…' : 'Remove'}</button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {confirmDialog}
    </div>
  );
};

const DocumentsTab = ({ projectId, user }) => {
  const [data, setData] = useState({ payApps: [], documents: [], storage: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [docType, setDocType] = useState('other');
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef(null);
  const { confirm, confirmDialogProps } = useConfirm();
  const search = usePageSearch('Search pay apps and documents…');

  // CM and above can remove documents, matching requireRole('construction_manager')
  // on the server. Derived from ROLE_RANK rather than a hand-kept list of role
  // names: a literal list silently omitted e3_direct_director (rank 3), and the
  // whole reason this codebase already has a ranking is so roles can be added
  // without hunting for every array that forgot one.
  // Immutable documents are refused by the server regardless of role, so this
  // only decides whether the affordance is drawn at all.
  const canDelete = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.construction_manager;

  const load = useCallback(async () => {
    try {
      setError(null);
      const d = await getProjectDocuments(projectId);
      setData(d);
    } catch (e) {
      console.error('Documents load failed:', e);
      setError(e.message || 'Could not load documents');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset immediately so picking the SAME file twice still fires a change
    // event — otherwise a failed upload cannot be retried without choosing a
    // different file, which reads as the button being broken.
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setNotice(null);
    try {
      await uploadProjectDocument(projectId, file, { docType });
      setNotice({ tone: 'success', text: `Uploaded ${file.name}` });
      await load();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const guardedDownload = async (fn, label) => {
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setNotice({ tone: 'danger', text: `Could not download ${label}: ${err.message}` });
    }
  };

  const onDelete = async (doc) => {
    const ok = await confirm({
      title: 'Remove document',
      message: `Remove "${doc.title || doc.filename}" from Project Documents? `
        + 'The file itself is retained in storage and can be restored.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    setBusyId(doc.id);
    setNotice(null);
    try {
      await deleteProjectDocument(doc.id);
      await load();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message || 'Could not remove the document' });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text-3)', fontSize: 14 }}>Loading documents…</div>;
  }

  return (
    <DocumentsView
      payApps={data.payApps || []}
      documents={data.documents || []}
      storage={data.storage || {}}
      error={error}
      notice={notice}
      uploading={uploading}
      docType={docType}
      canDelete={canDelete}
      busyId={busyId}
      onDocTypeChange={setDocType}
      onUploadClick={() => fileRef.current && fileRef.current.click()}
      onDownloadDocument={(d) => guardedDownload(
        () => downloadProjectDocument(d), d.filename || 'the document',
      )}
      onDelete={onDelete}
      fileInput={<input ref={fileRef} type="file" onChange={onPick} style={{ display: 'none' }} />}
      confirmDialog={<ConfirmDialog {...confirmDialogProps} />}
      search={search}
    />
  );
};

export default DocumentsTab;
